import { projectPath } from './project-tree';
import { habitPausedOn } from './habit-schedule';
import { todayIn } from '../utils/date-utils';
/**
 * Is this project, or anything above it, one the scheduler skips?
 *
 * Returns WHICH, because the sentence differs: a blocker sitting in a paused
 * project reads differently from one in a project you finished months ago,
 * even though neither holds anything back.
 */
function dormantAncestor(projectId, projects) {
    if (!projectId)
        return null;
    const path = projectPath(projects, projectId);
    if (path.some((p) => p.status === 'paused'))
        return 'paused';
    if (path.some((p) => p.status === 'archived' || p.status === 'completed'))
        return 'inactive';
    return null;
}
/**
 * One entry per dependency, in the order they were added.
 *
 * `projects` is optional: without it, project-level pause can't be detected
 * and those blockers simply read as pending, which is the safer of the two
 * wrong answers for a caller that doesn't have the data.
 */
export function describeDependencies(task, allTasks, projects = [], 
/** The user's today — a blocker whose pause has ended is holding again. */
today = todayIn()) {
    const byId = new Map(allTasks.map((t) => [t.id, t]));
    return (task.dependencies ?? []).map((id) => {
        const blocker = byId.get(id);
        if (!blocker) {
            return { id, title: 'a task that no longer exists', otherProject: null, reason: 'missing', holding: false };
        }
        const otherProject = blocker.project_id && blocker.project_id !== task.project_id
            ? projectPath(projects, blocker.project_id).at(-1)?.name ?? null
            : null;
        const reason = blocker.status === 'completed' ? 'completed'
            : blocker.is_archived ? 'archived'
                : habitPausedOn(blocker, today) ? 'paused'
                    : dormantAncestor(blocker.project_id, projects) === 'paused' ? 'project-paused'
                        : dormantAncestor(blocker.project_id, projects) === 'inactive' ? 'project-inactive'
                            : 'pending';
        return { id, title: blocker.title, otherProject, reason, holding: reason === 'pending' };
    });
}
/** Only the ones actually holding this task back. */
export function holdingBlockers(infos) {
    return infos.filter((b) => b.holding);
}
/**
 * Blockers that are being *ignored* — the ones the scheduler quietly treats
 * as satisfied because it can't see them. Worth showing: the usual way to hit
 * this is to pause a project and not realise you've released work elsewhere.
 */
export function ignoredBlockers(infos) {
    return infos.filter((b) => b.reason === 'paused' || b.reason === 'archived'
        || b.reason === 'project-paused' || b.reason === 'project-inactive'
        || b.reason === 'missing');
}
/** "Draft intro" · "Draft intro (Thesis)" — for a chip or a tooltip line. */
export function blockerLabel(info) {
    return info.otherProject ? `${info.title} (${info.otherProject})` : info.title;
}
/** Why this one isn't holding anything up, in words. Empty when it is. */
export function blockerNote(info) {
    switch (info.reason) {
        case 'completed': return 'done';
        case 'paused': return 'paused — not holding this back';
        case 'archived': return 'archived — not holding this back';
        case 'project-paused': return 'its project is paused — not holding this back';
        case 'project-inactive': return 'its project is archived or completed — not holding this back';
        case 'missing': return 'no longer exists';
        default: return '';
    }
}
/** Drop one dependency, leaving the rest in order. */
export function withoutDependency(task, dependencyId) {
    return (task.dependencies ?? []).filter((id) => id !== dependencyId);
}
//# sourceMappingURL=dependencies.js.map